import SmallSearchBar from "components/shared/SmallSearchBar";
import type { ChannelSnapshotData } from "containers/Channels/types";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import SubscriptionTable from "../../SubscriptionTable";
import type { SubscriptionTableRow } from "../../SubscriptionTable/types";
import { getSubscriptionTableColumns } from "./subscriptionTableConfig";
import { recalculateTableRows } from "./tableCalculations";
import TotalRevenueInput from "./TotalRevenueInput";

interface SubscriptionManagementModalBodyProps {
    isEditable: boolean;
    hasCompositePeriods: boolean;
    tableData: SubscriptionTableRow[];
    tableIsLoading: boolean;
    tablePage: number;
    tableRowsPerPage: number;
    tableTotalCount: number;
    totalRevenue: string;
    changedRows: Map<number, { consumptionCount?: number; avgConsumptionDuration?: number }>;
    channelSnapshot: ChannelSnapshotData | null;
    totalSubscriptionPoints: number;
    searchQuery: string;
    onTotalRevenueChange: (value: string) => void;
    onCellSave: (rowId: string | number, columnId: string, value: any) => void;
    onTablePageChange: (event: unknown, newPage: number) => void;
    onTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SubscriptionManagementModalBody = ({
    isEditable,
    hasCompositePeriods,
    tableData,
    tableIsLoading,
    tablePage,
    tableRowsPerPage,
    tableTotalCount,
    totalRevenue,
    changedRows,
    channelSnapshot,
    totalSubscriptionPoints,
    searchQuery,
    onTotalRevenueChange,
    onCellSave,
    onTablePageChange,
    onTableRowsPerPageChange,
    onSearchChange,
}: SubscriptionManagementModalBodyProps) => {
    const [t] = useTranslation();

    const columns = useMemo(() => getSubscriptionTableColumns(t, isEditable), [t, isEditable]);

    const adjustedTotalSubscriptionPointsRef = useRef(totalSubscriptionPoints);
    const calculatedRowsMapRef = useRef<Map<number, SubscriptionTableRow>>(new Map());

    // Reset adjusted total when backend total changes
    useEffect(() => {
        adjustedTotalSubscriptionPointsRef.current = totalSubscriptionPoints;
        calculatedRowsMapRef.current.clear();
    }, [totalSubscriptionPoints]);

    // Recalculate table rows with pending changes for real-time preview
    const displayedTableData = useMemo(() => {
        const channelRevenue = parseFloat(totalRevenue) || channelSnapshot?.totalRevenue || 0;

        const baseRows = tableData.map((backendRow) => {
            const titleId = Number(backendRow.id);
            const cachedRow = calculatedRowsMapRef.current.get(titleId);
            return changedRows.has(titleId) && cachedRow ? cachedRow : backendRow;
        });

        const result = recalculateTableRows({
            rows: baseRows,
            changedRows,
            channelTotalRevenue: channelRevenue,
            currentTotalSubscriptionPoints: adjustedTotalSubscriptionPointsRef.current,
        });

        result.rows.forEach((row) => {
            calculatedRowsMapRef.current.set(Number(row.id), row);
        });

        adjustedTotalSubscriptionPointsRef.current = result.newTotalSubscriptionPoints;

        return result.rows;
    }, [tableData, changedRows, totalRevenue, channelSnapshot?.totalRevenue]);

    return (
        <div className="modal-content">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    gap: "16px",
                }}
            >
                <div style={{ width: "300px" }}>
                    <SmallSearchBar filter={searchQuery} onSearch={onSearchChange} />
                </div>
                <TotalRevenueInput value={totalRevenue} onChange={onTotalRevenueChange} disabled={!isEditable} />
            </div>
            <SubscriptionTable
                columns={columns}
                data={displayedTableData}
                isLoading={tableIsLoading}
                page={tablePage}
                rowsPerPage={tableRowsPerPage}
                totalCount={tableTotalCount}
                onCellSave={onCellSave}
                onPageChange={onTablePageChange}
                onRowsPerPageChange={onTableRowsPerPageChange}
            />
        </div>
    );
};

export default SubscriptionManagementModalBody;
